/**
 * Validation utilities for use_endpoint requests
 * Consolidated from useEndpointUtils.ts and getMissingPathsForEndpoint.ts
 */

/**
 * Get a nested value from an object using dot-notation path
 */
function getByPath(obj: any, path: string): any {
  if (!path) return undefined;
  return path.split(".").reduce((acc: any, key) => acc?.[key], obj);
}

/**
 * Returns paths (dot-notation) of required fields that are missing or empty in formData
 * for the given endpoint schema. Supports nested required fields.
 */
export function getMissingPathsForEndpoint(
  formData: any,
  schema: { required?: string[]; properties?: Record<string, any> },
  prefix = ""
): string[] {
  const result: string[] = [];
  const required = schema?.required;
  const properties = schema?.properties;

  if (!required?.length || !properties) {
    return result;
  }

  for (const key of required) {
    const path = prefix ? `${prefix}.${key}` : key;
    const value = getByPath(formData, path);
    const propSchema = properties[key];

    if (propSchema?.type === "object" && propSchema?.properties) {
      const nestedMissing = getMissingPathsForEndpoint(
        value ?? {},
        propSchema,
        path
      );
      if (nestedMissing.length > 0) {
        result.push(...nestedMissing);
      } else if (value === undefined || value === null) {
        result.push(path);
      }
    } else {
      const isEmpty =
        value === undefined ||
        value === null ||
        value === "" ||
        (Array.isArray(value) && value.length === 0);
      if (isEmpty) {
        result.push(path);
      }
    }
  }

  return result;
}

/**
 * Returns paths (dot-notation) of required top-level keys that are missing or empty in formData.
 * Used when we only know required keys (e.g. from a previous backend validation error).
 */
export function getMissingPathsForRequiredKeys(
  formData: any,
  requiredKeys: string[]
): string[] {
  const result: string[] = [];
  for (const key of requiredKeys) {
    const value = formData?.[key];
    const isEmpty =
      value === undefined ||
      value === null ||
      value === "" ||
      (Array.isArray(value) && value.length === 0);
    if (isEmpty) {
      result.push(key);
    }
  }
  return result;
}

/**
 * Resolve endpoint schema from rpcs (object, Map, or array of { rpc_name/name/label, ...schema }).
 */
export function getEndpointSchema(
  rpcsRaw: unknown,
  endpointRpcName: string
): unknown {
  if (rpcsRaw == null || typeof rpcsRaw !== "object") return undefined;
  if (typeof (rpcsRaw as Map<string, unknown>).get === "function") {
    const v = (rpcsRaw as Map<string, unknown>).get(endpointRpcName);
    if (v != null) return v;
  }
  if (!Array.isArray(rpcsRaw)) {
    const rec = rpcsRaw as Record<string, unknown>;
    const v = rec[endpointRpcName];
    if (v != null) return v;
    const alt = rec[endpointRpcName.replace(/_/g, "-")];
    if (alt != null) return alt;
    return undefined;
  }
  const entry = (rpcsRaw as unknown[]).find((item: unknown) => {
    const o = item as Record<string, unknown> | null | undefined;
    return (
      o?.rpc_name === endpointRpcName ||
      o?.name === endpointRpcName ||
      o?.label === endpointRpcName
    );
  });
  const o = entry as { schema?: unknown } | null | undefined;
  return o != null ? (o.schema ?? entry) : undefined;
}

const REQUIRED_PROPERTY_REGEX = /"([^"]+)" is a required property/g;

/**
 * Extract required property names from backend validation error message.
 */
export function parseRequiredPropertiesFromError(message: string): string[] {
  const names: string[] = [];
  let m: RegExpExecArray | null;
  const re = new RegExp(REQUIRED_PROPERTY_REGEX.source, "g");
  while ((m = re.exec(message)) !== null) {
    if (!names.includes(m[1])) names.push(m[1]);
  }
  return names;
}

/**
 * Parse RPC response: result may be a JSON string or object. Returns payload for use_endpoint fill.
 */
export function parseRpcResultPayload(
  response: unknown
): Record<string, unknown> {
  const raw =
    typeof response === "object" && response !== null
      ? (response as Record<string, unknown>)
      : {};
  const resultPayload = raw.result;
  if (typeof resultPayload === "string") {
    try {
      const parsed = JSON.parse(resultPayload);
      return typeof parsed === "object" && parsed !== null ? parsed : {};
    } catch {
      return {};
    }
  }
  if (typeof resultPayload === "object" && resultPayload !== null) {
    return resultPayload as Record<string, unknown>;
  }
  return {};
}

/**
 * Format a dot-notation path (e.g. base_coin) as a label (e.g. Base Coin).
 */
export function formatPathAsLabel(path: string): string {
  const parts = path.split(".");
  const last = parts[parts.length - 1] ?? path;
  return last.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Build validation error message for missing fields
 */
export function buildValidationMessage(
  endpointRpcName: string,
  paths: string[]
): string {
  const labels = paths.map(formatPathAsLabel);
  return `Please fill in the required fields for "${endpointRpcName}": ${labels.join(", ")}`;
}
